/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import _ from 'lodash';
import $ from 'jquery';
import { ChronoUnit, LocalDate } from '@js-joda/core';

// first-party
import { CashFlowModel } from 'src/types/cash-flow.model';
import { CardModel } from 'src/types/card.model';
import { JaegerContext } from 'src/bw/project-jaeger/JaegerContainer';
import { FullUserModel } from 'src/types/user.model';
import UnitService from './unit.service';
import CalcFactory from 'src/bw/factories/calc.factory';
import FormatService from './format.service';

export enum EventSizes {
    SMALL = 1,
    DEFAULT = 1.45454545455,
    LARGE = 2
}

export interface JaegerBarHeights {
    income: number; // percentage
    expense: number;
    investment: number;
    debt: number;
    netWorth: number;
    incomeMax?: number;
    expenseMax?: number;
    investmentMax?: number;
    debtMax?: number;
    netWorthMax?: number;
}

export class JaegerService {
    public static TOTAL_MARGIN: number = 0.1; // percentile
    public static INTRA_BAR_PADDING: number = 3;
    public static MAX_BAR_HEIGHT: number = 70;
    public static TOP_BUTTON_HEIGHT: number = 70;
    public static CARD_ZONE_V_MARGINS: number = 8;
    public static CARD_NOMINAL_SIZE: number = 44;

    public static getPinSize(card?: CardModel): number {
        const cardMultiplier = _.get(card, 'attributes.icon') || EventSizes.DEFAULT;
        return this.CARD_NOMINAL_SIZE * cardMultiplier;
    }

    public static getViewPortWidth(): number {
        return UnitService.getViewportWidth() - UnitService.getDrawerWidth();
    }

    public static getViewPortWidthMinusMargin(): number {
        const viewportWidth: number = this.getViewPortWidth();
        return viewportWidth - viewportWidth * (this.TOTAL_MARGIN * 2);
    }

    public static getBarWidth(context: JaegerContext, user: FullUserModel, cashFlow: CashFlowModel[]): number {
        const x1 = UnitService.getJaegerX(context, user, cashFlow[0].startDate);
        const x2 = UnitService.getJaegerX(context, user, cashFlow[1].startDate);
        return JaegerService.getBarWidthFromX(x1, x2);
    }

    public static getBarWidthFromX(x1: number, x2: number): number {
        return x2 - x1 - this.INTRA_BAR_PADDING;
    }

    public static getBars(cashFlows: CashFlowModel[]): JaegerBarHeights[] {
        cashFlows = cashFlows.map((cf: CashFlowModel) => {
            cf.netWorth = CalcFactory.getNetWorth(cf);
            return cf;
        });

        const incomeMax: number = _.maxBy(cashFlows, 'income').income;
        const expenseMax: number = _.minBy(cashFlows, 'expense').expense;
        const incomeExpenseMax = Math.max(incomeMax, -expenseMax);
        const investmentMax: number = _.maxBy(cashFlows, 'investment').investment;
        const debtMax: number = _.minBy(cashFlows, 'debt').debt;
        const investmentDebtMax = Math.max(investmentMax, -debtMax);
        const netWorthMax: number = Math.max(
            _.maxBy(cashFlows, 'netWorth').netWorth,
            Math.abs(_.minBy(cashFlows, 'netWorth').netWorth)
        );

        return cashFlows.map((cf: CashFlowModel, index: number): JaegerBarHeights => {
            let height: any = {
                income: cf.income / incomeExpenseMax,
                expense: cf.expense / -incomeExpenseMax,
                investment: cf.investment / investmentDebtMax,
                debt: cf.debt / -investmentDebtMax,
                netWorth: netWorthMax <= 0 ? 0 : cf.netWorth / netWorthMax
            };
            if (index === 0) {
                height = {
                    ...height,
                    incomeMax,
                    expenseMax,
                    investmentMax,
                    debtMax,
                    netWorthMax
                };
            }
            return height;
        });
    }

    public static getCardZoneYLimits(viewPortHeight: number, cardHeight: number): { top: number; bottom: number } {
        return {
            top: this.TOP_BUTTON_HEIGHT + this.CARD_ZONE_V_MARGINS,
            bottom: viewPortHeight - cardHeight - this.CARD_ZONE_V_MARGINS - 16
        };
    }

    public static getCardYPercent(y: number, cardHeight: number, viewPortHeight: number): number {
        const limits = this.getCardZoneYLimits(viewPortHeight, cardHeight);
        y -= limits.top;
        return y / (limits.bottom - limits.top);
    }

    public static getCardYCoordinate(yPosition: number, cardHeight: number, viewPortHeight: number): number {
        const limits = this.getCardZoneYLimits(viewPortHeight, cardHeight);
        return yPosition * (limits.bottom - limits.top) + limits.top;
    }

    // get the X in screen coordinates and also make sure this is on the screen, in the middle
    public static getAbsoluteXFromDate(
        context: JaegerContext,
        user: FullUserModel,
        date: LocalDate,
        updateContext: (ctx: JaegerContext) => void
    ): number {
        // x + offset = width / 2
        // offset = width / 2 - x
        let x = UnitService.getJaegerX(context, user, date);
        const offset = Math.min(context.width / 2 - x, 0);
        x = Math.min(context.width / 2, x);
        updateContext({
            ...context,
            offset
        });
        return x + JaegerService.getJaegerLeft();
    }

    public static getJaegerWidth(): number {
        return $('.jaegerContainer').width();
    }

    public static getJaegerLeft(): number {
        return parseInt($('.jaegerContainer').css('marginLeft'));
    }

    public static getJaegerYTopBar(): number {
        return 0.666 * UnitService.getViewportHeight() + 80;
    }

    public static getJaegerYBottomBar(): number {
        return UnitService.getViewportHeight() - (0.333 * UnitService.getViewportHeight() - 136) / 2 - 56;
    }

    public static getJaegerYTimeline(): number {
        return 0.566 * UnitService.getViewportHeight();
    }

    public static timelineVisible(): boolean {
        const jaegerContainerWidth: number = document.getElementsByClassName('jaegerContainer')[0].clientWidth || 0;
        return jaegerContainerWidth > 1;
    }

    public static getJaegerCardY(card: CardModel, context: JaegerContext): number {
        return JaegerService.getCardYCoordinate(
            card.yPosition || 0.5,
            JaegerService.getPinSize(card),
            context.height * context.timelinePosition
        );
    }

    public static limitMagnification(magnification: number, context: JaegerContext): number {
        return Math.min(Math.max(context.minMagnification, magnification), 50);
    }

    public static getJaegerCashFlow(zoom: string, user: FullUserModel): CashFlowModel[] {
        return zoom === 'months' ? user.rawCashFlow : user.cashFlow;
    }

    /**
     * How often (cashflow periods) to show a label
     */
    public static dateLabelFrequency(context: JaegerContext, zoom: string, user: FullUserModel): number {
        const cashFlow: CashFlowModel[] = JaegerService.getJaegerCashFlow(zoom, user);
        const isMonthView: boolean = zoom === 'months';
        const barWidth: number = JaegerService.getBarWidth(context, user, cashFlow);
        let labelFrequency: number = isMonthView
            ? Math.max(Math.floor((1 / Math.ceil(barWidth / 20)) * 12), 1)
            : Math.max(Math.floor((1 / Math.ceil(barWidth / 20)) * 10), 1);

        if (!isMonthView) {
            // snap year sizes to 1, 2, 5, 10 years
            if (labelFrequency >= 10) labelFrequency = 10;
            else if (labelFrequency >= 5) labelFrequency = 5;
            else if (labelFrequency >= 2) labelFrequency = 2;
            else labelFrequency = 1;
        }
        return labelFrequency;
    }

    public static formatDates(dob: LocalDate, date1: LocalDate, date2?: LocalDate): string {
        const age1: number = dob.until(date1.minusDays(1), ChronoUnit.YEARS);
        let dateText: string = `Age ${age1} • ${FormatService.formatDateMonth(date1)}`;

        if (date2?.isAfter(date1)) {
            const age2: number = dob.until(date2.minusDays(1), ChronoUnit.YEARS);
            const dateText2: string = `Age ${age2} • ${FormatService.formatDateMonth(date2)}`;
            dateText += ` - ${dateText2}`;
        }

        return dateText;
    }
}
